import { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { t } from "@wr/i18n";
import {
  AuthCard,
  BrandLockup,
  Button,
  CountdownAction,
  FormBanner,
  formatCountdown,
  Mascot,
  SegmentedToggle,
  useCountdown,
  type BannerTone,
} from "@wr/ui";
import {
  AuthError,
  resendVerification,
  signInAnonymous,
  signInEmail,
  signUpEmail,
} from "../lib/auth";
import {
  CredentialsFields,
  emailLooksValid,
  passwordLongEnough,
  useCredentials,
} from "../components/CredentialsFields";
import { LegalAttestation } from "../components/LegalAttestation";
import { renderTemplate } from "../lib/rich";
import { useSession } from "../app/SessionProvider";
import { useOverlay } from "../app/OverlayProvider";
import { IN_PLACE_ROUTE_STATE } from "../app/RouteFocus";
import { sanitiseNext, withNext } from "../app/routing";

type Tab = "login" | "signup";

type Phase =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "error-generic"; message: string }
  | { kind: "error-unverified" }
  | { kind: "error-lockout"; seconds: number }
  | { kind: "error-email-taken" };

/**
 * Login / Sign up — design spec §6.
 *
 * **LOCKED: `/login` and `/signup` are the same component with a different
 * initial tab.** Two routes so both deep-link; one screen so the toggle is a
 * toggle and not a page load.
 *
 * Two error rules that look contradictory and are not — Quinn, this asymmetry
 * is deliberate and PRD-directed:
 *
 *  - **Login never discloses whether an account exists.** One generic message
 *    for wrong email, wrong password and no such account. Never a field-level
 *    "wrong password", never a red email field.
 *  - **Sign-up DOES disclose "email already in use"**, with two ways out. PRD
 *    §4.2 asks for it and the flow is unusable otherwise.
 *
 * There is also **no "remember me" checkbox** (§6, LOCKED): the session cookie
 * is persistent by default. A game does not ask a 15-year-old to opt in to
 * staying logged in.
 */
export function AuthScreen({ initialTab }: { initialTab: Tab }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const { reload } = useSession();
  const { showToast } = useOverlay();

  const [tab, setTab] = useState<Tab>(initialTab);
  const [credentials, setCredentials] = useCredentials({
    email: params.get("email") ?? "",
  });
  const [phase, setPhase] = useState<Phase>({ kind: "idle" });
  const [showTosError, setShowTosError] = useState(false);
  const [resendKey, setResendKey] = useState(0);
  /** The dead-link banner's own resend (T-200 N1). Mirrors `/verify`'s states. */
  const [linkResend, setLinkResend] = useState<"idle" | "sending" | "rate-limited">("idle");

  const next = sanitiseNext(params.get("next"));
  /**
   * ── The verification callback's two outcomes, and why they must differ ─────
   *
   * `GET /api/auth/verify-email` redirects to the callback we baked into the
   * mail — always `/login?verified=1[&next=…]` — and on failure it *appends*
   * `&error=TOKEN_EXPIRED|INVALID_TOKEN`. Until T-200 this screen read only
   * `verified`, so an expired link rendered "Email verified. Log in to start
   * playing." and the login that followed was refused: a dead end reached by
   * being told you had succeeded (QA T-195 **N1** — the three screenshots were
   * byte-identical).
   *
   * `error` therefore **suppresses** the success banner rather than sitting
   * beside it. `/verify` and `/reset` already treat any `error` value as one
   * dead-link state (Better Auth returns a single code for expiry and for
   * garbage, and ResetScreen's accepted deviation records that); this mirrors
   * them rather than inventing a third convention.
   */
  const linkError = params.get("error");
  const verified = params.get("verified") === "1" && linkError === null;
  const bannerState = location.state as { banner?: string; bannerTone?: BannerTone } | null;
  const flash = bannerState?.banner ?? null;
  /**
   * `success` for `/reset` (§8.2), `info` for the post-conversion arrival
   * (§14.2 item 7 — the account exists and is waiting on verification, which is
   * information rather than good news).
   */
  const flashTone: BannerTone = bannerState?.bannerTone ?? "success";

  useEffect(() => {
    setTab(initialTab);
  }, [initialTab]);

  /**
   * **The arrival is consumed, not merely read** (§14.2 item 7, LOCKED).
   *
   * Two things arrive with the post-conversion handoff and neither may outlive
   * the visit:
   *
   * 1. **`?email=`.** The address is prefilled above from the query string and
   *    then taken out of the URL in the same breath — `next` and everything
   *    else are kept. An email address in a URL sits in history on exactly the
   *    shared school laptop §14.1 ruling C worries about; consuming it is what
   *    makes the prefill affordable, and Ivy chose that over banning it.
   * 2. **The `location.state` banner.** §8.2's mechanism is specified as
   *    "survives exactly **one** route change", and item 7 rule 4 states the
   *    consequence: *a reload leaves a clean login screen*. `BrowserRouter`
   *    stores router state in `history.state.usr`, which the browser persists
   *    across a reload — so without this the banner comes back on F5 and the
   *    contract is only true for the first half of its name. Dropping it here
   *    is what makes the reload clean, and it fixes the same latent behaviour
   *    for `/reset`'s success banner, which uses the identical mechanism.
   *
   * `history.replaceState` rather than `navigate(…, { replace: true })` on
   * purpose: this must not create a router transition. A transition would
   * re-run `RouteFocus` (§15 rule 11) on a screen the player has already
   * arrived at, and would take the banner off the screen they are looking at
   * rather than off the next reload. React Router keeps its in-memory
   * `location.state`, so the banner stays rendered for this visit — which is
   * the whole point of it.
   *
   * `location.key` is the dependency, not `params`: a new `URLSearchParams` is
   * produced on every render, and the address bar is what this reads anyway.
   */
  useEffect(() => {
    const current = new URL(window.location.href);
    // `has` then `delete`: `URLSearchParams.delete` only started returning a
    // boolean recently and does not everywhere, so its result is not evidence.
    const hadEmail = current.searchParams.has("email");
    if (hadEmail) current.searchParams.delete("email");

    const historyState = window.history.state as { usr?: Record<string, unknown> } | null;
    const routerState = historyState?.usr;
    const hadBanner =
      typeof routerState === "object" && routerState !== null && "banner" in routerState;

    if (!hadEmail && !hadBanner) return;

    const spentState = hadBanner
      ? {
          ...historyState,
          usr: Object.fromEntries(
            Object.entries(routerState as Record<string, unknown>).filter(
              ([key]) => key !== "banner" && key !== "bannerTone",
            ),
          ),
        }
      : historyState;

    window.history.replaceState(
      spentState,
      "",
      `${current.pathname}${current.search}${current.hash}`,
    );
  }, [location.key]);

  const submitting = phase.kind === "submitting";
  const formValid =
    emailLooksValid(credentials.email) &&
    (tab === "login" || passwordLongEnough(credentials.password)) &&
    credentials.password.length > 0;

  function switchTab(value: Tab) {
    if (submitting) return;
    setTab(value);
    setPhase({ kind: "idle" });
    setShowTosError(false);
    // Keeps the URL honest so a refresh lands on the tab you are looking at.
    //
    // `IN_PLACE_ROUTE_STATE` because this is the one route change in the app
    // that is not an arrival: §6 LOCKS `/login` and `/signup` as one screen
    // with two routes, so the toggle is a toggle. Without the marker, §15 rule
    // 11 would pull focus off the control the player just operated.
    void navigate(withNext(value === "login" ? "/login" : "/signup", next), {
      replace: true,
      ...IN_PLACE_ROUTE_STATE,
    });
  }

  async function submit() {
    if (tab === "signup" && !credentials.tosAccepted) {
      setShowTosError(true);
      return;
    }
    setShowTosError(false);
    setPhase({ kind: "submitting" });

    try {
      if (tab === "login") {
        await signInEmail(credentials.email, credentials.password);
        await reload();
        // Let the splash's matrix decide: this screen does not get an opinion
        // about whether the account still needs verification or a nickname.
        void navigate(withNext("/", next), { replace: true });
        return;
      }

      /**
       * `next` goes to the API as well as into the URL below (§14.2 item 5,
       * QA T-177 N8). Without it the two roads out of this sign-up disagree:
       * a player who deep-linked to `/settings`, signed up, and then finished
       * in their mail app landed on `/lobby`, while the one who stayed in the
       * tab landed on `/settings`. "The system forgot an interruption it
       * created", in Ivy's words — the mail is the only place that memory can
       * live for the player who closed the tab.
       */
      await signUpEmail(credentials.email, credentials.password, true, next);
      // `?next=` survives the `/verify` gate too (§5, LOCKED): a player who
      // deep-linked, signed up and then verified should still land where they
      // were going, not on the lobby.
      void navigate(withNext(`/verify?email=${encodeURIComponent(credentials.email)}`, next), {
        replace: true,
      });
    } catch (caught) {
      setPhase(classify(caught, tab));
    }
  }

  /**
   * Resend from the dead-link banner (T-200 N1).
   *
   * **The address has to come from the form**, because a verification callback
   * carries no email — and putting one in that URL is exactly what §14.1
   * ruling C rules out (an address in browser history on a shared laptop). So
   * an empty or malformed field is not a dead button: it marks the field
   * touched and moves focus there, which is the same validation affordance the
   * form already uses, rather than a disabled control with no stated reason.
   *
   * **Judgment call, flagged for Ivy in T-200:** §6's banner register has no
   * slot for "you arrived on a dead verification link", so the interaction is
   * modelled on §7's resend and the copy is §7's existing `verify.linkExpired`.
   * No new strings.
   */
  async function resendFromDeadLink() {
    if (!emailLooksValid(credentials.email)) {
      setCredentials({ ...credentials, emailTouched: true });
      document.getElementById("auth-email")?.focus();
      return;
    }

    setLinkResend("sending");
    try {
      await resendVerification(credentials.email, next);
      setLinkResend("idle");
      showToast(t("verify.resent"));
    } catch (caught) {
      // 429 is the per-account 60s window (§7 `rate-limited`), which is a
      // state with its own line, not a failure to report generically.
      setLinkResend(caught instanceof AuthError && caught.status === 429 ? "rate-limited" : "idle");
    }
  }

  async function continueAsGuest() {
    setPhase({ kind: "submitting" });
    try {
      await signInAnonymous();
      await reload();
      void navigate(withNext("/", next), { replace: true });
    } catch (caught) {
      setPhase(classify(caught, tab));
    }
  }

  return (
    <div className="wr-auth-layout">
      {/* Desktop-only decorative panel (§6). Nothing interactive lives here. */}
      <aside className="wr-auth-panel" aria-hidden="true">
        <Mascot size={220} />
        <p className="wr-display-lg">{t("auth.panel.tagline")}</p>
        <span className="wr-auth-panel__wordmark">
          <BrandLockup
            size="md"
            wordy={t("app.wordy")}
            royale={t("app.royale")}
            appName={t("app.name")}
          />
        </span>
      </aside>

      <div className="wr-auth-layout__form">
        <div className="wr-form-column wr-stack" style={{ gap: 24 }}>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <BrandLockup
              size="md"
              wordy={t("app.wordy")}
              royale={t("app.royale")}
              appName={t("app.name")}
            />
          </div>

          <AuthCard>
            <SegmentedToggle
              label={t("auth.tabsLabel")}
              value={tab}
              disabled={submitting}
              onChange={switchTab}
              options={[
                { value: "login", label: t("auth.tab.login") },
                { value: "signup", label: t("auth.tab.signup") },
              ]}
            />

            {/* §15 rule 11: the `AuthCard` headline is this screen's
                announcement — it has no `<main>` to hold one. */}
            <h1 className="wr-display-lg" data-route-heading>
              {tab === "login" ? t("auth.title.login") : t("auth.title.signup")}
            </h1>

            {/*
              `takeFocus={false}` on both: these are **arrival** banners, not
              responses to a submit. §15 rule 11 puts focus on the heading above
              — a banner that grabs it back would fight the route-change rule
              and, on the post-conversion arrival, would move focus past the
              heading that is supposed to be the announcement. `FormBanner`'s
              own doc names this case; the submit-response banners in
              `PhaseBanner` below keep the default.
            */}
            {verified ? (
              <FormBanner tone="success" takeFocus={false} testId="auth-verified">
                {t("auth.verified")}
              </FormBanner>
            ) : null}
            {linkError !== null ? (
              <FormBanner
                tone="error"
                takeFocus={false}
                testId="auth-link-expired"
                actions={
                  <Button
                    variant="secondary"
                    size="sm"
                    loading={linkResend === "sending"}
                    loadingLabel={t("common.working")}
                    data-testid="auth-link-resend"
                    onClick={() => void resendFromDeadLink()}
                  >
                    {t("verify.resend")}
                  </Button>
                }
              >
                {t("verify.linkExpired")}
              </FormBanner>
            ) : null}
            {linkResend === "rate-limited" ? (
              <FormBanner tone="warning" takeFocus={false} testId="auth-link-rate-limited">
                {t("verify.rateLimited")}
              </FormBanner>
            ) : null}
            {flash ? (
              <FormBanner tone={flashTone} takeFocus={false} testId="auth-flash">
                {flash}
              </FormBanner>
            ) : null}

            <PhaseBanner
              phase={phase}
              email={credentials.email}
              resendKey={resendKey}
              onResend={() => {
                /**
                 * The target is this screen's own `?next=` (§14.2 item 5, QA
                 * T-177 N7). This resend fires from `error-unverified`, which
                 * is reached by a player who deep-linked, was sent here by the
                 * guard, and is one step further along the *same* interruption
                 * — so the mail owes them the same landing the first one did.
                 */
                void resendVerification(credentials.email, next).catch(() => undefined);
                setResendKey((value) => value + 1);
              }}
              onLoginInstead={() => switchTab("login")}
              onResetPassword={() =>
                void navigate(`/forgot?email=${encodeURIComponent(credentials.email)}`)
              }
            />

            <form
              className="wr-stack"
              style={{ gap: 16 }}
              onSubmit={(event) => {
                event.preventDefault();
                if (formValid && !submitting) void submit();
              }}
            >
              <CredentialsFields
                idPrefix="auth"
                mode={tab}
                state={credentials}
                onChange={setCredentials}
                submitting={submitting}
                showTosError={showTosError}
                {...(tab === "login"
                  ? { forgotHref: `/forgot?email=${encodeURIComponent(credentials.email)}` }
                  : {})}
              />

              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                loading={submitting}
                loadingLabel={t("common.working")}
                disabled={!formValid || phase.kind === "error-lockout"}
                data-testid="auth-submit"
              >
                {tab === "login" ? t("auth.cta.login") : t("auth.cta.signup")}
              </Button>
            </form>

            <div className="wr-divider wr-body-xs">{t("auth.or")}</div>

            {/* LOCKED order: primary → divider → guest. Guest is never above
                the primary and never a text link — it is a legitimate
                first-class entry (PRD §3). */}
            <Button
              variant="secondary"
              size="lg"
              fullWidth
              disabled={submitting}
              data-testid="continue-as-guest"
              onClick={() => void continueAsGuest()}
            >
              {t("auth.cta.guest")}
            </Button>
          </AuthCard>

          {/*
            **On both tabs from T-234**, which is what §6's layout drawing has
            always shown. The line it replaced rendered on `login` only, and the
            tab that did not get it is the tab with `Create account` on it —
            while `Continue as guest`, which also creates an account, is on
            both. See `LegalAttestation` for the full rule.
          */}
          <LegalAttestation testId="auth-legal" />
        </div>
      </div>
    </div>
  );
}

/** The `FormBanner` slot at the top of the card — one banner per phase (§6). */
function PhaseBanner({
  phase,
  resendKey,
  onResend,
  onLoginInstead,
  onResetPassword,
}: {
  phase: Phase;
  email: string;
  resendKey: number;
  onResend: () => void;
  onLoginInstead: () => void;
  onResetPassword: () => void;
}) {
  if (phase.kind === "error-generic") {
    return (
      <FormBanner tone="error" testId="auth-error">
        {phase.message}
      </FormBanner>
    );
  }

  if (phase.kind === "error-unverified") {
    return (
      <FormBanner
        tone="warning"
        testId="auth-unverified"
        actions={
          <CountdownAction
            key={resendKey}
            seconds={60}
            size="sm"
            variant="secondary"
            label={t("verify.resend")}
            waitingLabel={() => t("verify.resendWait")}
            onAction={onResend}
            testId="auth-resend"
          />
        }
      >
        {t("auth.error.unverified")}
      </FormBanner>
    );
  }

  if (phase.kind === "error-lockout") {
    return <LockoutBanner seconds={phase.seconds} />;
  }

  if (phase.kind === "error-email-taken") {
    return (
      <FormBanner
        tone="info"
        testId="auth-email-taken"
        actions={
          <>
            <Button variant="secondary" size="sm" onClick={onLoginInstead}>
              {t("auth.action.loginInstead")}
            </Button>
            <Button variant="ghost" size="sm" onClick={onResetPassword}>
              {t("auth.action.resetPassword")}
            </Button>
          </>
        }
      >
        {t("auth.error.emailTaken")}
      </FormBanner>
    );
  }

  return null;
}

/**
 * The lockout countdown (§6 `error-lockout`): a live `m:ss` in `mono-md`, and
 * the primary button re-enables **without a reload** when it reaches zero.
 */
function LockoutBanner({ seconds }: { seconds: number }) {
  const { remaining } = useCountdown(seconds);
  return (
    <FormBanner tone="error" testId="auth-lockout">
      {/* §3.2 LOCKED: numbers are Archivo Black or DM Mono, never DM Sans — so
          the timer is substituted as a node, not interpolated into the string. */}
      {renderTemplate(t("auth.error.lockout"), {
        time: <span className="wr-mono-md">{formatCountdown(remaining)}</span>,
      })}
    </FormBanner>
  );
}

/**
 * Server failure → one of the design's named states.
 *
 * The default branch is the generic message on purpose: an unrecognised auth
 * code must never fall through to whatever sentence the library happened to
 * send, because that is exactly how "no user found with that email" reaches a
 * login screen that is not allowed to say so.
 */
function classify(caught: unknown, tab: Tab): Phase {
  const error = caught instanceof AuthError ? caught : null;

  if (error?.status === 429) {
    return { kind: "error-lockout", seconds: error.retryAfterSeconds ?? 900 };
  }

  if (error?.authCode === "EMAIL_NOT_VERIFIED" || error?.status === 403) {
    return { kind: "error-unverified" };
  }

  if (
    tab === "signup" &&
    (error?.authCode === "USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL" || error?.status === 422)
  ) {
    return { kind: "error-email-taken" };
  }

  if (tab === "login") {
    // LOCKED: no enumeration. Every login failure is the same sentence.
    return { kind: "error-generic", message: t("auth.error.credentials") };
  }

  return {
    kind: "error-generic",
    message: caught instanceof Error ? caught.message : t("error.unexpected"),
  };
}
